import { generatePath, useNavigate } from 'react-router';

import { PATHS, PATIENT_PROFILE_SCROLL_KEY_PREFIX } from '@/shared/config';
import { useAppSelector, useUserTimezone } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';

import { composedModel } from '@/entities/composed';
import { organizationModel } from '@/entities/organization';
import {
  isConsultationOpenable,
  type TranscriptorConsultation,
  useOpenConsultation,
  useStaleConsultationDetection,
  useTranscriptorConsultations,
} from '@/entities/transcriptor';

import { getRecordingsPreview } from '../lib';

export const usePatientRecordingsCard = (patientID: string) => {
  const navigate = useNavigate();
  const userTimezone = useUserTimezone();
  const transcriptorV2Enabled = useAppSelector(
    organizationModel.selectors.selectTranscriptorV2Enabled,
  );

  const canViewArchive = useAppSelector(
    composedModel.selectors.selectCanViewPatientRecordingsArchive(patientID),
  );

  const { consultations, hiddenConsultations, visibleTotal, isLoaded } =
    useTranscriptorConsultations(patientID, {
      transcriptorV2Enabled,
      includeHidden: canViewArchive,
    });
  const { isConsultationStale, forceStop } = useStaleConsultationDetection();
  const openConsultation = useOpenConsultation({
    patientID,
    scrollKey: `${PATIENT_PROFILE_SCROLL_KEY_PREFIX}${patientID}`,
    eventName: 'PatientProfilePageRecordingsWidgetItemClicked',
  });

  const previewConsultations = getRecordingsPreview(consultations);

  const handleCardClick = () => {
    trackEvent({
      eventName: 'PatientProfilePageRecordingsWidgetClicked',
      properties: { patient_id: patientID },
    });

    navigate(generatePath(PATHS.patientRecordings, { patientID }));
  };

  const handleItemClick = (consultation: TranscriptorConsultation) => {
    if (!isConsultationOpenable(consultation)) {
      return;
    }

    openConsultation(consultation);
  };

  return {
    previewConsultations,
    visibleTotal,
    isVisible: isLoaded && (visibleTotal > 0 || hiddenConsultations.length > 0),
    userTimezone,
    isConsultationStale,
    forceStop,
    handleCardClick,
    handleItemClick,
  };
};
